import React, { FC, useMemo } from 'react';
import { ColorPicker as ReactColorPicker, useColor } from 'react-color-palette';
import 'react-color-palette/lib/css/styles.css';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverCloseButton,
  PopoverBody,
  PopoverArrow,
  Icon,
  PopoverFooter,
  Button,
  useDisclosure,
  VStack,
  HStack,
  chakra,
  Tooltip,
  Text,
} from '@chakra-ui/react';
import { Edit, Info } from 'react-feather';
import { hex } from 'wcag-contrast';
import round from 'lodash/round';
import { useScreenSizeState } from '@olo-web/client-state';

type T = {
  currentColor: string;
  onChange: (value: string) => void;
};

export const ColorPicker: FC<T> = ({ currentColor, onChange }) => {
  const [color, setColor] = useColor('hex', currentColor);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { isSmDown } = useScreenSizeState();

  const [W, H] = useMemo(() => {
    if (isSmDown) return [300, 112];
    return [300, 180];
  }, [isSmDown]);

  const handleUpdate = () => {
    onChange(color.hex);
    onClose();
  };

  const contrastRatio = useMemo(() => {
    if (!color?.hex) return null;
    return round(hex(color?.hex, '#fff'), 2);
  }, [color]);

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      placement={isSmDown ? 'top' : 'bottom'}
    >
      <PopoverTrigger>
        <Button
          variant="ghost"
          rightIcon={<Icon as={Edit} h={4} w={4} />}
          aria-label="open color selector"
        >
          Select color
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverBody w={W}>
          <ReactColorPicker
            width={W}
            height={H}
            color={color}
            onChange={setColor}
            hideHSV
            hideRGB
          />
          {contrastRatio && (
            <VStack w={W} mb={2}>
              <HStack>
                <Text>
                  <chakra.span fontWeight="semibold">Contrast ratio:</chakra.span> {contrastRatio}:1
                </Text>
                <Tooltip
                  placement="top"
                  label="This measures how much contrast the color has against a white background.  In order to comply with web accessibility standards, you should aim for a ratio of at least 3:1."
                >
                  <Icon as={Info} />
                </Tooltip>
              </HStack>
            </VStack>
          )}
        </PopoverBody>
        <PopoverFooter>
          <Button w="100%" fontSize="md" colorScheme="primary" onClick={handleUpdate}>
            Update
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};
